import { CalendarModuleCommandBarProps } from './schema';
import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import { logUsage } from 'owa-analytics';
import { assertNever } from 'owa-assert';
import { surfaceStore } from 'owa-calendar-surface-surface-store';
import { ControlIcons } from 'owa-control-icons';
import { getNextDateRange, getPreviousDateRange } from 'owa-datetime-utils';
import { DisplayOption, FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import loc, { format } from 'owa-localize';
import { goToNextDayText } from 'owa-locstrings/lib/strings/gotonextdaytext.locstring.json';
import { goToPreviousDayText } from 'owa-locstrings/lib/strings/gotopreviousdaytext.locstring.json';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import dateRangeToDisplayString from 'owa-timestrip/lib/utils/dateRangeToDisplayString';
import {
    goToPreviousWeekStringText,
    goToNextWeekStringText,
    goToPreviousMonthText,
    goToNextMonthStringText,
} from 'owa-calendar-shared-strings/lib/strings/calendarSurfaceCommandBar.locstring.json';

export function getSurfaceNavigationMenuItems(props: CalendarModuleCommandBarProps) {
    const userOptions = getUserConfiguration().UserOptions;

    const currentDateRange = surfaceStore.dateRange;
    const currentDateRangeType = surfaceStore.dateRangeType;

    const previousDateRange = dateRangeToDisplayString(
        getPreviousDateRange(currentDateRange, currentDateRangeType),
        currentDateRangeType,
        userOptions.WeekStartDay,
        userOptions.ShowWeekNumbers,
        userOptions.FirstWeekOfYear as any,
        false
    );
    const nextDateRange = dateRangeToDisplayString(
        getNextDateRange(currentDateRange, currentDateRangeType),
        currentDateRangeType,
        userOptions.WeekStartDay,
        userOptions.ShowWeekNumbers,
        userOptions.FirstWeekOfYear as any,
        false
    );

    let currentViewPreviousLabel: string = '';
    let currentViewNextLabel: string = '';
    switch (currentDateRangeType) {
        case DateRangeType.Day:
            currentViewPreviousLabel = loc(goToPreviousDayText);
            currentViewNextLabel = loc(goToNextDayText);
            break;
        case DateRangeType.WorkWeek:
            currentViewPreviousLabel = loc(goToPreviousWeekStringText);
            currentViewNextLabel = loc(goToNextWeekStringText);
            break;
        case DateRangeType.Week:
            currentViewPreviousLabel = loc(goToPreviousWeekStringText);
            currentViewNextLabel = loc(goToNextWeekStringText);
            break;
        case DateRangeType.Month:
            currentViewPreviousLabel = loc(goToPreviousMonthText);
            currentViewNextLabel = loc(goToNextMonthStringText);
            break;
        default:
            assertNever(currentDateRangeType);
    }

    const previousLabel = format(currentViewPreviousLabel, '\n' + previousDateRange);
    const nextLabel = format(currentViewNextLabel, '\n' + nextDateRange);

    const onPrevious = (ev: React.MouseEvent<HTMLDivElement>) => {
        logUsage('SurfaceCommandBar_Previous', {
            currentDateRangeType: currentDateRangeType,
        });
        const newDateRange = getPreviousDateRange(currentDateRange, currentDateRangeType);
        props.onDateRangeChange(newDateRange);
    };

    const onNext = (ev: React.MouseEvent<HTMLDivElement>) => {
        logUsage('SurfaceCommandBar_Next', {
            currentDateRangeType: currentDateRangeType,
        });
        const newDateRange = getNextDateRange(currentDateRange, currentDateRangeType);
        props.onDateRangeChange(newDateRange);
    };

    const isMonthDateRange = surfaceStore.dateRangeType === DateRangeType.Month;

    return [
        new FilterableMenuItem({
            [FilterableMenuItemArgs.icon]: isMonthDateRange ? ControlIcons.Up : ControlIcons.Back,
            [FilterableMenuItemArgs.onClick]: onPrevious,
            [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
            [FilterableMenuItemArgs.ariaLabel]: previousLabel,
            [FilterableMenuItemArgs.title]: previousLabel,
            [FilterableMenuItemArgs.className]: 'flipForRtl',
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.icon]: isMonthDateRange
                ? ControlIcons.Down
                : ControlIcons.Forward,
            [FilterableMenuItemArgs.onClick]: onNext,
            [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
            [FilterableMenuItemArgs.ariaLabel]: nextLabel,
            [FilterableMenuItemArgs.title]: nextLabel,
            [FilterableMenuItemArgs.className]: 'flipForRtl',
        }),
    ];
}
